import { useMemo } from 'react';

import { allowedFileTypes } from '../../../../../../../../../../backend/src/matter/doc/allowed-extensions';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetFilterOptions } from '@/api/queries/useGetMetadataFilterOptions';
import { DateRangeFilter } from '@/new/components/date-range-filter';
import { DocumentTypeFilter } from '@/new/components/doc-type-filter';
import { FilterSwitch } from '@/new/components/filter-switch';
import { Icon } from '@/new/components/icon';
import { Label } from '@/new/components/shadcn/label';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectTrigger,
  MultiSelectValue,
} from '@/new/components/shadcn/multi-select';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/new/components/shadcn/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';
import type { DocFilters, MetadataFieldFilter } from '@/types';

const fileExtensionOptions = allowedFileTypes
  .map((fileType) => {
    const extension = fileType.replace('.', '').toLowerCase();
    return extension.toUpperCase();
  })
  .sort((a, b) => a.localeCompare(b));

export const DocumentFiltersPopup = ({
  matterId,
  activeFilters,
  setFilters,
  allDocumentTypes,
  docThemes,
}: {
  matterId: string;
  activeFilters: DocFilters;
  setFilters: (v: DocFilters) => void;
  allDocumentTypes: string[];
  docThemes: string[];
}) => {
  const processedValue =
    activeFilters.processed === true ? 'true' : activeFilters.processed === false ? 'false' : undefined;

  // Fetch Relativity filter options
  const { data: filterOptions, isLoading: isLoadingRelativity } = useGetFilterOptions({ matterId });

  // Only include choice fields (fields with availableOptions) - text search is done via main search bar
  const choiceFields = useMemo(() => {
    if (!filterOptions?.relativityFields.fields) return [];

    return filterOptions.relativityFields.fields.filter(
      (field) =>
        filterOptions.relativityFields.availableOptions[field.fieldName] &&
        filterOptions.relativityFields.availableOptions[field.fieldName].length > 0,
    );
  }, [filterOptions]);

  const hasRelativityChoiceFields = choiceFields.length > 0;

  // Helper to get current filter value for a field
  const getFilterValue = (fieldName: string): MetadataFieldFilter | undefined => {
    return activeFilters.metadataFilters?.find((f) => f.field === fieldName);
  };

  // Helper to update a specific field's filter
  const updateFieldFilter = (fieldName: string, value: MetadataFieldFilter['value'] | undefined) => {
    trackCustomEvent(TrackingEventsV2.ApplyFiltersOnDocumentsPage);

    const currentFilters = activeFilters.metadataFilters ?? [];
    let newFilters: MetadataFieldFilter[];

    const isEmpty = value === undefined || (Array.isArray(value) && value.length === 0);
    if (isEmpty) {
      // Remove the filter for this field
      newFilters = currentFilters.filter((f) => f.field !== fieldName);
    } else {
      // Update or add the filter
      const existingIndex = currentFilters.findIndex((f) => f.field === fieldName);
      if (existingIndex >= 0) {
        newFilters = [...currentFilters];
        newFilters[existingIndex] = { field: fieldName, value };
      } else {
        newFilters = [...currentFilters, { field: fieldName, value }];
      }
    }

    setFilters({
      ...activeFilters,
      metadataFilters: newFilters.length > 0 ? newFilters : undefined,
    });
  };

  // Regular filters content
  const regularFiltersContent = (
    <>
      {/* Key Facts Filter */}
      <FilterSwitch
        id="keyfacts-switch"
        label="Contains Key Facts"
        checked={activeFilters.hasKeyFacts === true}
        onCheckedChange={(checked) => {
          trackCustomEvent(TrackingEventsV2.ApplyFiltersOnDocumentsPage);
          setFilters({
            ...activeFilters,
            hasKeyFacts: checked ? true : undefined,
          });
        }}
      />

      {/* Relevant Facts Filter */}
      <FilterSwitch
        id="relevantfacts-switch"
        label="Contains Relevant Facts"
        checked={activeFilters.hasRelevantFacts === true}
        onCheckedChange={(checked) => {
          trackCustomEvent(TrackingEventsV2.ApplyFiltersOnDocumentsPage);
          setFilters({
            ...activeFilters,
            hasRelevantFacts: checked ? true : undefined,
          });
        }}
      />

      <div>
        <Label className="mb-2 block">Document Type</Label>
        <DocumentTypeFilter
          selectedTypes={activeFilters.documentTypes?.map((type) => type.toUpperCase()) || []}
          setSelectedTypes={(types) => {
            trackCustomEvent(TrackingEventsV2.ApplyFiltersOnDocumentsPage);
            setFilters({
              ...activeFilters,
              documentTypes: types.length > 0 ? types : undefined,
            });
          }}
          allDocumentTypes={allDocumentTypes}
        />
      </div>

      <div>
        <Label className="mb-2 block">Processing Status</Label>
        <Select
          value={processedValue}
          onValueChange={(value) => {
            trackCustomEvent(TrackingEventsV2.ApplyFiltersOnDocumentsPage);
            setFilters({
              ...activeFilters,
              processed: value === 'true' ? true : value === 'false' ? false : undefined,
            });
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select processing status..." />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="true">Processed Only</SelectItem>
              <SelectItem value="false">Unprocessed Only</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="mb-2 block">Themes</Label>
        <MultiSelect
          values={activeFilters.documentThemes ?? []}
          onValuesChange={(values) => {
            trackCustomEvent(TrackingEventsV2.ApplyFiltersOnDocumentsPage);
            setFilters({
              ...activeFilters,
              documentThemes: values,
            });
          }}
        >
          <MultiSelectTrigger className="w-full">
            <MultiSelectValue placeholder="Select themes..." />
          </MultiSelectTrigger>
          <MultiSelectContent>
            <MultiSelectGroup>
              {docThemes.map((theme) => (
                <MultiSelectItem key={theme} value={theme}>
                  {theme}
                </MultiSelectItem>
              ))}
            </MultiSelectGroup>
          </MultiSelectContent>
        </MultiSelect>
      </div>

      <div>
        <Label className="mb-2 block">File Types</Label>
        <MultiSelect
          values={activeFilters.extensions?.map((ext) => ext.toUpperCase()) || []}
          onValuesChange={(values) => {
            trackCustomEvent(TrackingEventsV2.ApplyFiltersOnDocumentsPage);
            setFilters({
              ...activeFilters,
              extensions: values.length > 0 ? values : undefined,
            });
          }}
        >
          <MultiSelectTrigger className="w-full">
            <MultiSelectValue placeholder="Select file types..." />
          </MultiSelectTrigger>
          <MultiSelectContent>
            <MultiSelectGroup>
              {fileExtensionOptions.map((fileExtension) => (
                <MultiSelectItem key={fileExtension} value={fileExtension}>
                  {fileExtension}
                </MultiSelectItem>
              ))}
            </MultiSelectGroup>
          </MultiSelectContent>
        </MultiSelect>
      </div>

      <div>
        <Label className="mb-2 block">Document Date Range</Label>
        <DateRangeFilter
          startDate={activeFilters.startDate ? new Date(activeFilters.startDate) : null}
          endDate={activeFilters.endDate ? new Date(activeFilters.endDate) : null}
          setStartDate={(date) => {
            trackCustomEvent(TrackingEventsV2.ApplyFiltersOnDocumentsPage);
            setFilters({
              ...activeFilters,
              startDate: date?.toISOString() || null,
            });
          }}
          setEndDate={(date) => {
            trackCustomEvent(TrackingEventsV2.ApplyFiltersOnDocumentsPage);
            setFilters({
              ...activeFilters,
              endDate: date?.toISOString() || null,
            });
          }}
        />
      </div>
    </>
  );

  // Relativity filters content - only choice fields with multi-select
  const relativityFiltersContent = (
    <>
      {isLoadingRelativity && <p className="text-sm text-gray-500">Loading Relativity fields...</p>}

      {choiceFields.map((field) => {
        const currentFilter = getFilterValue(field.fieldName);
        const selectedValues = Array.isArray(currentFilter?.value) ? (currentFilter.value as string[]) : [];
        const options = filterOptions?.relativityFields.availableOptions[field.fieldName] ?? [];

        return (
          <div key={field.fieldName} className="mt-4">
            <div className="mb-3 flex items-center gap-2">
              <Icon className="lucide--database h-3.5 w-3.5 text-purple-600" />
              <Label className="mb-0">{field.displayName}</Label>
            </div>
            <MultiSelect
              values={selectedValues}
              onValuesChange={(values) => {
                updateFieldFilter(field.fieldName, values.length > 0 ? values : undefined);
              }}
            >
              <MultiSelectTrigger className="w-full">
                <MultiSelectValue placeholder={`Select ${field.displayName.toLowerCase()}...`} />
              </MultiSelectTrigger>
              <MultiSelectContent>
                <MultiSelectGroup>
                  {options.map((option) => (
                    <MultiSelectItem key={option} value={option}>
                      {option}
                    </MultiSelectItem>
                  ))}
                </MultiSelectGroup>
              </MultiSelectContent>
            </MultiSelect>
          </div>
        );
      })}

      {!isLoadingRelativity && choiceFields.length === 0 && (
        <p className="text-sm text-gray-500">No Relativity choice fields configured for this subset.</p>
      )}
    </>
  );

  // If no Relativity choice fields, just render regular filters without tabs
  if (!hasRelativityChoiceFields && !isLoadingRelativity) {
    return <>{regularFiltersContent}</>;
  }

  return (
    <Tabs defaultValue="regular" className="w-full">
      <TabsList className="mb-4 w-full justify-start">
        <TabsTrigger value="regular">Regular Filters</TabsTrigger>
        <TabsTrigger value="relativity" className="flex items-center gap-1.5">
          <Icon className="lucide--database h-3.5 w-3.5 text-purple-600" />
          Relativity Filters
        </TabsTrigger>
      </TabsList>

      <TabsContent value="regular" className="space-y-4">
        {regularFiltersContent}
      </TabsContent>

      <TabsContent value="relativity" className="space-y-4">
        {relativityFiltersContent}
      </TabsContent>
    </Tabs>
  );
};
